/**
 * Route-aware helpers for landing section navigation interactions.
 *
 * What: Centralizes section-link href generation and section-jump behavior across landing-shared UI.
 * Why: Ensures consistent navigation from both landing and non-landing routes without duplicating route checks.
 * When: Update when landing route semantics, section hash patterns, or scroll offset rules change.
 */
import { getScrollMargin, scrollToSection } from '@/utils/smoothScroll';

/**
 * Determine if the current route is the landing page root.
 */
function isLandingRoute(pathname: string): boolean {
  return pathname === '/';
}

/**
 * Build a route-aware href for a landing section.
 */
export function getLandingSectionHref(
  sectionId: string,
  pathname: string
): string {
  const sectionHash = `#${sectionId}`;
  return isLandingRoute(pathname) ? sectionHash : `/${sectionHash}`;
}

interface NavigateToLandingSectionOptions {
  sectionId: string;
  pathname: string;
  navigate: (to: string) => void;
  isMobile?: boolean;
}

/**
 * Navigate to a landing section from any current route.
 */
export function navigateToLandingSection({
  sectionId,
  pathname,
  navigate,
  isMobile = false,
}: NavigateToLandingSectionOptions): void {
  if (isLandingRoute(pathname)) {
    scrollToSection(sectionId, {
      offset: getScrollMargin(isMobile),
      duration: 600,
    });
    return;
  }

  navigate(getLandingSectionHref(sectionId, pathname));
}
